// src/pages/admin/editorTabs/EditorTab.jsx
import React, { useMemo, useState } from "react";
import { sectionTemplates } from "../../../data/database";

const availableSectionTypes = [
  { type: "hero", name: "Hero Section", icon: "🚀", description: "Main banner section" },
  { type: "features", name: "Features Section", icon: "⚡", description: "Showcase features" },
  { type: "testimonials", name: "Testimonials", icon: "💬", description: "Customer reviews" },
  { type: "gallery", name: "Gallery Section", icon: "🖼️", description: "Portfolio showcase" },
  { type: "cta", name: "Call to Action", icon: "🎯", description: "Conversion section" },
  { type: "categorysection", name: "Category Section", icon: "🛍️", description: "Show demo items + open catalog" },
  { type: "locationsection", name: "Location Section", icon: "📍", description: "Address + map + contact" },
  { type: "serviceSection", name: "Service Section", icon: "🧩", description: "Services / Carousel / Pricing" },
];

export default function EditorTab({
  sections,
  limits,
  onToggleSection,
  onMoveSection,
  onEditSection,
  onOpenTemplateSelector,
  onRemoveSection,
  onAddSection,

  nav,
  onOpenNavEditor,

  footer,
  previewSlug,
  onOpenFooterEditor,

  whatsapp,
  setWhatsapp,
}) {
  const [showAddSection, setShowAddSection] = useState(false);

  const activeCount = useMemo(() => sections.filter((s) => s.enabled).length, [sections]);

  const maxSections = limits?.maxSections;
  const isAtMaxSections = typeof maxSections === "number" && maxSections >= 0 && sections.length >= maxSections;

  return (
    <div className="space-y-6">
      {/* Sections */}
      <div className="bg-white rounded-2xl shadow-sm border p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-gray-900">Sections</div>
          <div className="text-xs text-gray-500">
            {activeCount} active
            {typeof maxSections === "number" ? ` • limit ${maxSections}` : ""}
          </div>
        </div>

        <div className="space-y-3">
          {sections.map((section, index) => {
            const templateData = sectionTemplates?.[section.type]?.[section.template];
            return (
              <div key={section.id} className="border rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 capitalize">{section.type}</div>
                    <div className="text-xs text-gray-500 truncate">{templateData?.name || section.template}</div>
                    <div className="text-xs text-gray-400 truncate mt-1">{section.config?.title || "No title"}</div>
                  </div>

                  <button
                    onClick={() => onToggleSection(index)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      section.enabled ? "bg-green-500" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        section.enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => onMoveSection(index, "up")}
                      disabled={index === 0}
                      className="px-2 py-1 rounded-lg border text-sm disabled:opacity-40"
                    >
                      Up
                    </button>
                    <button
                      onClick={() => onMoveSection(index, "down")}
                      disabled={index === sections.length - 1}
                      className="px-2 py-1 rounded-lg border text-sm disabled:opacity-40"
                    >
                      Down
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-1 ml-auto">
                    <button
                      onClick={() => onEditSection(section)}
                      className="px-2 py-1 rounded-lg bg-blue-50 text-blue-700 text-sm hover:bg-blue-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onOpenTemplateSelector(index)}
                      className="px-2 py-1 rounded-lg bg-purple-50 text-purple-700 text-sm hover:bg-purple-100"
                    >
                      Template
                    </button>
                    <button
                      onClick={() => onRemoveSection(index)}
                      className="px-2 py-1 rounded-lg bg-red-50 text-red-700 text-sm hover:bg-red-100"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {showAddSection ? (
            <div className="border-2 border-dashed rounded-xl p-3">
              <div className="font-semibold text-gray-900 mb-2">Add Section</div>

              {isAtMaxSections && (
                <div className="mb-2 text-sm text-red-600">
                  You reached the max sections limit for this page.
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                {availableSectionTypes.map((s) => (
                  <button
                    key={s.type}
                    onClick={() => {
                      if (isAtMaxSections) return;
                      onAddSection(s.type);
                      setShowAddSection(false);
                    }}
                    disabled={isAtMaxSections}
                    className="p-2 border rounded-lg hover:bg-gray-50 text-left disabled:opacity-50"
                  >
                    <div className="text-lg">{s.icon}</div>
                    <div className="text-sm font-medium">{s.name}</div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => setShowAddSection(false)}
                className="mt-3 w-full py-2 rounded-lg border"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddSection(true)}
              className="w-full py-2 rounded-xl border-2 border-dashed hover:bg-gray-50"
            >
              Add New Section
            </button>
          )}
        </div>
      </div>

      {/* Navbar */}
      <div className="bg-white rounded-2xl shadow-sm border p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-gray-900">Navigation</div>
          <button
            onClick={onOpenNavEditor}
            disabled={!nav}
            className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm hover:bg-blue-100 disabled:opacity-50"
          >
            Edit Navigation
          </button>
        </div>

        <div className="text-xs text-gray-500">
          Navigation renders once at the top of the page.
        </div>

        <div className="mt-3 text-sm text-gray-700 space-y-1">
          <div>
            <span className="font-medium">Brand:</span> {nav?.config?.brandName || "Not set"}
          </div>
          <div>
            <span className="font-medium">Links:</span>{" "}
            {Array.isArray(nav?.config?.links) ? nav.config.links.length : 0}
          </div>
          <div>
            <span className="font-medium">Manager Login:</span>{" "}
            {nav?.config?.showManagerLogin === false ? "No" : "Yes"}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white rounded-2xl shadow-sm border p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-gray-900">Footer</div>
          <button
            onClick={onOpenFooterEditor}
            className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm hover:bg-blue-100"
          >
            Edit Footer
          </button>
        </div>

        <div className="text-xs text-gray-500">
          Footer renders once at the bottom of the page.
        </div>

        <div className="mt-3 text-sm text-gray-700 space-y-1">
          <div>
            <span className="font-medium">Business:</span>{" "}
            {footer?.config?.businessName || "Not set"}
          </div>
          <div className="truncate">
            <span className="font-medium">Privacy link:</span>{" "}
            {footer?.config?.privacyPolicyLink || `/${previewSlug}/privacy`}
          </div>
          <div>
            <span className="font-medium">Enabled:</span>{" "}
            {footer?.enabled === false ? "No" : "Yes"}
          </div>
        </div>
      </div>

      {/* WhatsApp */}
      <div className="bg-white rounded-2xl shadow-sm border p-5">
        <div className="font-bold text-gray-900 mb-3">WhatsApp Button</div>

        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={!!whatsapp?.enabled}
            onChange={(e) =>
              setWhatsapp((prev) => ({ ...(prev || {}), enabled: e.target.checked }))
            }
          />
          Enable WhatsApp Button
        </label>

        <input
          placeholder="Phone (9725xxxxxxx)"
          value={whatsapp?.config?.phone || ""}
          onChange={(e) =>
            setWhatsapp((prev) => ({
              ...(prev || {}),
              config: {
                ...((prev && prev.config) || {}),
                phone: e.target.value,
              },
            }))
          }
          className="w-full px-3 py-2 border rounded-lg mb-2"
        />

        <input
          placeholder="Default message"
          value={whatsapp?.config?.message || ""}
          onChange={(e) =>
            setWhatsapp((prev) => ({
              ...(prev || {}),
              config: {
                ...((prev && prev.config) || {}),
                message: e.target.value,
              },
            }))
          }
          className="w-full px-3 py-2 border rounded-lg mb-2"
        />

        <select
          value={whatsapp?.style?.position || "right"}
          onChange={(e) =>
            setWhatsapp((prev) => ({
              ...(prev || {}),
              style: {
                ...((prev && prev.style) || {}),
                position: e.target.value,
              },
            }))
          }
          className="w-full px-3 py-2 border rounded-lg"
        >
          <option value="right">Right</option>
          <option value="left">Left</option>
        </select>
      </div>
    </div>
  );
}